import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStats } from '../../../../context/StatsContext';
import Preloader from '../../../../components/Preloader/Preloader';
import './MyTree.css';

const TreeNode = ({ node, isRoot = false, depth = 0, onNodeClick }) => {
  const navigate = useNavigate();

  const displayName = node.name || '??';
  const namePart = displayName.includes('@') ? displayName.split('@')[0] : displayName;
  const initials = node.status !== 'empty'
    ? namePart.slice(0, 2).toUpperCase()
    : '+';

  const handleClick = () => {
    if (node.status === 'empty') {
      navigate('/dashboard/create-account');
    } else if (!isRoot && node.status !== 'empty' && onNodeClick) {
      onNodeClick(node);
    }
  };

  const isClickable = node.status === 'empty' || (!isRoot && node.status !== 'empty');

  return (
    <div className={`mt-branch ${isRoot ? 'mt-branch--root' : ''}`}>
      <div className="mt-node">
        <div className="mt-node__header">
          <div
            className={`mt-avatar mt-avatar--${node.status} ${isClickable ? 'mt-avatar--clickable' : ''}`}
            onClick={handleClick}
            title={node.status === 'empty' ? 'Click to add a member' : (!isRoot ? 'Click to view tree from this member' : '')}
          >
            <span className="mt-avatar__text">{initials}</span>
            {isRoot && <div className="mt-avatar__ring" />}
          </div>
        </div>
        <div className="mt-node__info">
          <span className="mt-node__name">{node.name}</span>
          {node.position && !isRoot && (
            <span className={`mt-badge mt-badge--${node.position}`}>
              {node.position === 'left' ? '← Left' : 'Right →'}
            </span>
          )}
        </div>
      </div>

      {depth < 2 && node.children && node.children.length > 0 && (
        <div className="mt-children">
          {node.children.map((child, i) => (
            child ? <TreeNode key={i} node={child} depth={depth + 1} onNodeClick={onNodeClick} /> : null
          ))}
        </div>
      )}
    </div>
  );
};

const countNodes = (node) => {
  if (!node || node.status === 'empty') return { total: 0, left: 0, right: 0 };
  let total = 1, left = 0, right = 0;
  if (node.children) {
    node.children.forEach(c => {
      if (!c) return;
      const sub = countNodes(c);
      total += sub.total;
      if (c.position === 'left') left += sub.total;
      if (c.position === 'right') right += sub.total;
    });
  }
  return { total, left, right };
};

const MyTree = () => {
  const { treeData, loadingTree: loading } = useStats();
  const [currentRoot, setCurrentRoot] = useState(null);
  const [navHistory, setNavHistory] = useState([]);

  if (loading) {
    return (
      <div className="mt-page">
        <Preloader message="Fetching your network..." />
      </div>
    );
  }

  if (!treeData || !treeData.name) {
    return (
      <div className="mt-page">
        <div className="mt-empty">
          <div className="mt-empty__icon">🌳</div>
          <h3>No Tree Data</h3>
          <p>Your referral network will appear here once you start building your team.</p>
        </div>
      </div>
    );
  }

  const activeRoot = currentRoot || treeData;

  const handleNodeClick = (node) => {
    setNavHistory(prev => [...prev, activeRoot]);
    setCurrentRoot(node);
  };

  const handleBackClick = () => {
    if (navHistory.length === 0) return;
    const newHistory = [...navHistory];
    const prevRoot = newHistory.pop();
    setNavHistory(newHistory);
    // If the popped root was the user's base root, set currentRoot to null
    setCurrentRoot(prevRoot.name === treeData.name ? null : prevRoot);
  };

  const handleResetClick = () => {
    setNavHistory([]);
    setCurrentRoot(null);
  };

  // Stats update dynamically based on the active/focused root
  const rootStats = countNodes(activeRoot);
  const totalDescendants = rootStats.total > 0 ? rootStats.total - 1 : 0;

  return (
    <div className="mt-page">
      {/* Header */}
      <div className="mt-header">
        <div className="mt-header__left">
          <h1 className="mt-header__title">My Network Tree</h1>
          <p className="mt-header__sub">Your binary referral network visualization</p>
          
          <div className="mt-header__nav-actions">
            {navHistory.length > 0 && (
              <button className="mt-back-btn" onClick={handleBackClick}>
                <span className="mt-back-btn__icon">←</span> Back
              </button>
            )}
            {currentRoot && (
              <button className="mt-reset-btn" onClick={handleResetClick}>
                <span className="mt-reset-btn__icon">↺</span> Reset to Root
              </button>
            )}
          </div>
        </div>

        <div className="mt-header__stats">
          <div className="mt-stat">
            <span className="mt-stat__num">{rootStats.left}</span>
            <span className="mt-stat__label">Left-Side Members</span>
          </div>
          <div className="mt-stat">
            <span className="mt-stat__num">{totalDescendants}</span>
            <span className="mt-stat__label">Total Members</span>
          </div>
          <div className="mt-stat">
            <span className="mt-stat__num">{rootStats.right}</span>
            <span className="mt-stat__label">Right-Side Members</span>
          </div>
        </div>
      </div>

      {/* Tree Card */}
      <div className="mt-card">
        <div className="mt-card__hint">
          <span>👆</span> Scroll horizontally to explore the full tree on smaller screens
        </div>
        <div className="mt-tree-scroll">
          <div className="mt-tree-flow">
            <TreeNode node={activeRoot} isRoot={true} onNodeClick={handleNodeClick} />
          </div>

        </div>
      </div>

      {/* Legend */}
      <div className="mt-legend">
        <div className="mt-legend__item">
          <div className="mt-legend__dot mt-legend__dot--root" />
          <span>Focused Root</span>
        </div>
        <div className="mt-legend__item">
          <div className="mt-legend__dot mt-legend__dot--active" />
          <span>Active Member</span>
        </div>
        <div className="mt-legend__item">
          <div className="mt-legend__dot mt-legend__dot--empty" />
          <span>Empty Slot</span>
        </div>
      </div>
    </div>
  );
};

export default MyTree;